import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageProvider';
import { useAuth0 } from './client';
import { LoginForm, type LoginSubmit } from './login/LoginForm';
import { LoginHero } from './login/LoginHero';

const DEFAULT_RETURN_TO = '/';

/**
 * The sign-in screen. Auth lives in `client.tsx`; this file is presentation
 * plus the `returnTo` hand-off. Language comes from `LanguageProvider`, which
 * already reads `?language=` on load, so the `/login?returnTo=…&language=…`
 * contract is served without any resolution logic here.
 */
/**
 * Callers may still pass the old `variant` / copy-override props; the screen is
 * patient-portal only now and the copy comes from the dictionary, so they are
 * accepted and ignored rather than breaking the call sites.
 */
type AuthLoginPageProps = {
  variant?: 'staff' | 'admin' | 'portal';
};

export function AuthLoginPage(_props: AuthLoginPageProps = {}) {
  const { isAuthenticated, loginWithPassword } = useAuth0();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const returnTo = searchParams.get('returnTo') || DEFAULT_RETURN_TO;

  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, navigate, returnTo]);

  const handleSubmit: LoginSubmit = async ({ email, password, remember }) => {
    await loginWithPassword(email, password, { remember, language });
    navigate(returnTo, { replace: true });
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-surface">
      <div className="mx-auto grid min-h-screen w-full max-w-6xl grid-cols-1 lg:grid-cols-2 lg:items-stretch">
        <LoginForm onSubmit={handleSubmit} />
        {/* Below lg the form carries its own welcome; the panel would only push the fold down. */}
        <div className="hidden lg:flex lg:flex-col">
          <LoginHero />
        </div>
      </div>
    </div>
  );
}
